import { sorted } from "helpers";
import { ChefGroupInfo } from "hooks/useDefaultMaterialLimits";
import {
  MaterialLimit,
  MaterialLimitCoefficientCreate,
  MaterialLimitRead,
} from "src/store/api/generatedApi";
import { coefficientSignature } from "src/utils/materialLimitOverrides";

export type MaterialLimitBoundField =
  "min_mass" | "soft_min_mass" | "soft_max_mass" | "max_mass";

export const MATERIAL_LIMIT_BOUND_FIELDS: MaterialLimitBoundField[] = [
  "min_mass",
  "soft_min_mass",
  "soft_max_mass",
  "max_mass",
];

/** One column of the matrix: a material-limit type, shared across chef groups. */
export interface MaterialLimitColumn {
  signature: string;
  name: string;
  coefficientsSignature: string;
  isExplicitlyNamed: boolean;
  coefficients: MaterialLimitCoefficientCreate[];
}

/** One row of the matrix: a chef group and its limits keyed by signature. */
export interface ChefGroupRow {
  id: number;
  chefGroupName: string;
  productGroupName: string;
  steelGradeNames: string[];
  limitsBySignature: Record<string, MaterialLimit>;
}

/**
 * Distinct material-limit types, sorted with explicitly-named ones first. A
 * limit is explicitly named when its stored name differs from the signature
 * the backend derives from its coefficients.
 */
export const deriveLimitColumns = (
  limits: MaterialLimitRead[]
): MaterialLimitColumn[] => {
  const columnsBySignature = new Map<string, MaterialLimitColumn>();
  for (const limit of limits) {
    const signature = coefficientSignature(limit);
    if (!columnsBySignature.has(signature)) {
      columnsBySignature.set(signature, {
        signature,
        name: limit.name ?? limit.coefficients_signature,
        coefficientsSignature: limit.coefficients_signature,
        isExplicitlyNamed:
          limit.name != null && limit.name !== limit.coefficients_signature,
        coefficients: limit.coefficients,
      });
    }
  }
  return sorted(
    [...columnsBySignature.values()],
    (column) => `${column.isExplicitlyNamed ? "0" : "1"}_${column.name}`
  );
};

/** Drop the product-group prefix from a chef-group name (it's the group row). */
const stripProductGroupPrefix = (
  chefGroupName: string,
  productGroupName: string
): string => {
  if (productGroupName !== "" && chefGroupName.startsWith(productGroupName)) {
    const remainder = chefGroupName
      .slice(productGroupName.length)
      .replace(/^[\s(]+/, "")
      .replace(/\)\s*$/, "")
      .trim();
    return remainder === "" ? chefGroupName : remainder;
  }
  return chefGroupName;
};

export const buildChefGroupRows = (
  limits: MaterialLimit[],
  chefGroupsById: Record<number, ChefGroupInfo>
): ChefGroupRow[] => {
  const limitsByChefGroup = new Map<number, Record<string, MaterialLimit>>();
  for (const limit of limits) {
    const bySignature = limitsByChefGroup.get(limit.chef_group_id) ?? {};
    bySignature[coefficientSignature(limit)] = limit;
    limitsByChefGroup.set(limit.chef_group_id, bySignature);
  }
  const rows = Object.entries(chefGroupsById).map(
    ([id, info]): ChefGroupRow => ({
      id: Number(id),
      chefGroupName: stripProductGroupPrefix(info.name, info.productGroupName),
      productGroupName: info.productGroupName,
      steelGradeNames: info.steelGradeNames,
      limitsBySignature: limitsByChefGroup.get(Number(id)) ?? {},
    })
  );
  return sorted(rows, (row) => `${row.productGroupName}/${row.chefGroupName}`);
};

/** A scope to view/edit: the mix (whole charge, null) or a specific basket. */
export type MaterialLimitScope = number | null;

export const boundForScope = (
  limit: MaterialLimit,
  scope: MaterialLimitScope
) => limit.bounds.find((bound) => (bound.basket_id ?? null) === scope);

/** Whether the limit carries a non-empty bound in a scope other than this one. */
export const hasBoundsOutsideScope = (
  limit: MaterialLimit,
  scope: MaterialLimitScope
): boolean =>
  limit.bounds.some(
    (bound) =>
      (bound.basket_id ?? null) !== scope &&
      (bound.min_mass != null ||
        bound.soft_min_mass != null ||
        bound.soft_max_mass != null ||
        bound.max_mass != null)
  );

/** A stable string for comparing two limits' bounds and hardness. */
export const limitFingerprint = (limit: MaterialLimit): string =>
  JSON.stringify({
    hardness: limit.hardness,
    bounds: [...limit.bounds]
      .sort((a, b) => (a.basket_id ?? -1) - (b.basket_id ?? -1))
      .map((bound) => [
        bound.basket_id,
        bound.min_mass,
        bound.soft_min_mass,
        bound.soft_max_mass,
        bound.max_mass,
      ]),
  });

/** Whether a limit differs from its ScrapChef default (absent on either side counts). */
export const differsFromScrapchefDefault = (
  working: MaterialLimit | undefined,
  scrapchef: MaterialLimit | undefined
): boolean => {
  if (working === undefined && scrapchef === undefined) {
    return false;
  }
  if (working === undefined || scrapchef === undefined) {
    return true;
  }
  return limitFingerprint(working) !== limitFingerprint(scrapchef);
};

/**
 * For each column (signature), the bound fields that at least one chef group
 * sets at the given scope — so columns can hide the slots every row leaves
 * empty and size to what remains.
 */
export const activeBoundFieldsByColumn = (
  limits: MaterialLimit[],
  scope: MaterialLimitScope
): Record<string, MaterialLimitBoundField[]> => {
  const fieldsBySignature = new Map<string, Set<MaterialLimitBoundField>>();
  for (const limit of limits) {
    const signature = coefficientSignature(limit);
    const used = fieldsBySignature.get(signature) ?? new Set();
    const bound = boundForScope(limit, scope);
    if (bound !== undefined) {
      for (const field of MATERIAL_LIMIT_BOUND_FIELDS) {
        if (bound[field] != null) {
          used.add(field);
        }
      }
    }
    fieldsBySignature.set(signature, used);
  }
  return Object.fromEntries(
    [...fieldsBySignature.entries()].map(([signature, used]) => [
      signature,
      MATERIAL_LIMIT_BOUND_FIELDS.filter((field) => used.has(field)),
    ])
  );
};

const formatCoefficient = (
  value: number,
  formatNumber?: (value: number) => string
): string => {
  if (value === 1) return "";
  if (value === -1) return "-";
  if (formatNumber) return formatNumber(value);
  let formatted = value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  if (formatted.startsWith(".")) formatted = `0${formatted}`;
  if (formatted.startsWith("-.")) formatted = `-0${formatted.slice(1)}`;
  return formatted;
};

export const CONFLICT_LABEL_MAX_TERMS = 4;

/** Formats a limit's coefficients with translated class names.
 * The optional term limit replaces omitted terms with an ellipsis. */
export const formatSignatureLabel = ({
  coefficients,
  classNameById,
  translate,
  maxTerms,
  formatNumber,
}: {
  coefficients: MaterialLimitCoefficientCreate[];
  classNameById: Record<number, string>;
  translate: (key: string) => string;
  maxTerms?: number;
  formatNumber?: (value: number) => string;
}): string => {
  const terms = sorted(
    coefficients,
    (coefficient) =>
      classNameById[coefficient.material_constraint_class_id] ?? ""
  ).map((coefficient) => {
    const className =
      classNameById[coefficient.material_constraint_class_id] ??
      String(coefficient.material_constraint_class_id);
    return `${formatCoefficient(coefficient.coefficient, formatNumber)}${translate(className)}`;
  });
  const shown =
    maxTerms !== undefined && terms.length > maxTerms
      ? [...terms.slice(0, maxTerms), "…"]
      : terms;
  return shown.join(" + ").replace(/ \+ -/g, " - ");
};

/** Formats a limit name and its coefficient classes.
 * The optional term limit replaces omitted classes with an ellipsis. */
export const formatLimitLabel = (
  column: MaterialLimitColumn,
  classNameById: Record<number, string>,
  translate: (key: string) => string,
  maxTerms?: number
): string => {
  const quantity = formatSignatureLabel({
    coefficients: column.coefficients,
    classNameById,
    translate,
    maxTerms,
  });
  return column.isExplicitlyNamed
    ? `${translate(column.name)} (${quantity})`
    : quantity;
};

export const buildLimitLabelsBySignature = (
  columns: MaterialLimitColumn[],
  classNameById: Record<number, string>,
  translate: (key: string) => string
): Record<string, string> =>
  Object.fromEntries(
    columns.map((column) => [
      column.coefficientsSignature,
      formatLimitLabel(
        column,
        classNameById,
        translate,
        CONFLICT_LABEL_MAX_TERMS
      ),
    ])
  );
